"use client";

import * as React from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { AnimatePresence } from "framer-motion";
import type { BlockBase } from "@/cms/blocks/registry/types";
import { removeAt, insertAtEnd } from "../utils";
import type { ProductPickerItem, SelectedProduct } from "../types";
import { ProductPickerOverlay } from "../overlays/ProductPickerOverlay";
import { ProductSortableRow } from "../overlays/ProductSortableRow";
import { PlusSVG } from "@/components/ui/SVG";
import { productReferences } from "@/cms/lib/productCards";
import { useProductTeasers } from "@/hooks/useProductTeasers";

export function ProductCardEditor({
  block,
  onChange,
}: {
  block: BlockBase;
  onChange: (patch: (data: any) => any) => void;
  locale: "en" | "fa";
}) {
  const data = block.data as any;
  const products = React.useMemo<SelectedProduct[]>(
    () =>
      productReferences(data).map((p) => ({
        ...p,
        partNumber: p.partNumber ?? "",
      })),
    [data],
  );
  const { products: currentProducts } = useProductTeasers(
    products.map((p) => p.productId),
  );
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 250, tolerance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
    }),
  );
  const productRowIds = React.useMemo(
    () =>
      products.map(
        (product, index) => `${product.productId || "product"}-${index}`,
      ),
    [products],
  );

  const commitProducts = React.useCallback(
    (next: SelectedProduct[]) => {
      onChange((prev) => ({
        ...prev,
        products: next,
        productId: undefined,
        partNumber: undefined,
        heroImage: undefined,
      }));
    },
    [onChange],
  );

  const handleRemove = React.useCallback(
    (index: number) => {
      if (index < 0 || index >= products.length) return;
      commitProducts(removeAt(products, index));
    },
    [products, commitProducts],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = productRowIds.indexOf(String(active.id));
      const newIndex = productRowIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      commitProducts(arrayMove(products, oldIndex, newIndex));
    },
    [productRowIds, products, commitProducts],
  );

  const handleToggleFromOverlay = React.useCallback(
    (option: ProductPickerItem) => {
      onChange((prev) => {
        const current = productReferences(prev).map((p) => ({
          ...p,
          partNumber: p.partNumber ?? "",
        }));
        const targetIndex = current.findIndex(
          (item: any) => item.productId === option.id,
        );
        const nextProducts =
          targetIndex >= 0
            ? removeAt(current, targetIndex)
            : insertAtEnd(current, {
                productId: option.id,
                partNumber: option.part_number,
              });
        return {
          ...prev,
          products: nextProducts,
          productId: undefined,
          partNumber: undefined,
          heroImage: undefined,
        };
      });
    },
    [onChange],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="opacity-70">Selected products</span>
        {products.length > 0 ? (
          <span className="text-xs text-gray-500">
            {products.length} selected
          </span>
        ) : null}
      </div>

      {products.length > 0 ? (
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={productRowIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="space-y-2">
              {products.map((product, index) => (
                <ProductSortableRow
                  key={productRowIds[index]}
                  id={productRowIds[index]}
                  product={product}
                  imageUrl={
                    currentProducts[product.productId]?.heroAsset?.url ||
                    currentProducts[product.productId]?.hero_image
                  }
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="rounded border border-dashed border-gray-200 py-8 text-center text-sm text-gray-500">
          No products selected yet.
        </div>
      )}

      <button
        type="button"
        className="border-pinkCustom text-pinkCustom hover:bg-pinkCustom/10 flex w-full items-center justify-center gap-2 rounded border border-dashed bg-white px-3 py-2 text-sm font-semibold transition"
        onClick={() => setPickerOpen(true)}
      >
        <PlusSVG className="w-4" svgColor="var(--color-pinkCustom)" />
        <span>{products.length ? "Manage products" : "Add products"}</span>
      </button>

      <AnimatePresence>
        {pickerOpen ? (
          <ProductPickerOverlay
            selectedProducts={products}
            onToggleProduct={handleToggleFromOverlay}
            onClose={() => setPickerOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  );
}
